import { forwardRef, useImperativeHandle } from 'react';

import { useIntl } from 'react-intl';

import { Col, Form, Input, Row, Select, Spin } from 'antd';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import FormSelect from '@src/components/Form/components/FormSelect';
import { KeyValueWidget } from '@src/components/KeyValueWidget';
import LocationCombiner from '@src/components/Location/LocationCombiner';
import { FORM_LAYOUT_DEFAULT } from '@src/constants/commons';
import DivisionsSelector from '@src/containers/DivisionsSelector';
import DropDownTriggers from '@src/containers/DropDownTriggers';

import DataTypeSelector from '../../components/Shared/DataTypeSelector';
import UsersTypeSelector from '../../components/Shared/UsersTypeSelector';
import { DASHBOARDS_FORMS, USERS_TYPE } from '../../constants';
import { useCounterNumberComponent } from './hooks';

const CounterNumberComponent = forwardRef(({ chartId, divId, boardId }, formRef) => {
  const intl = useIntl();
  const {
    type,
    form,
    buildBody,
    handleClear,
    selectedSchedules,
    setSelectedSchedules,
    setLoadingSchedules,
    initialScheduleIds,
    eventTypes,
    eventTypesLoading,
    watchDivisionIds,
    eventTypesWithNumComponents,
    orgId,
    fetchedChartData,
    handleTypeChange,
    handleDivisionChange,
    groupOptions,
    groupsLoading,
    handlePopupScrollGroups,
    watchUsersType,
    resetUsersAndGroups,
    loading,
    filterDataOptions,
    handleTriggerChange,
    handleFormChange,
  } = useCounterNumberComponent({
    chartId,
    divId,
    boardId,
  });

  useImperativeHandle(
    formRef,
    () => ({
      ...form,
      handleClear,
      submit: () => form.submit(),
    }),
    [handleClear, form],
  );

  return (
    <Spin spinning={loading}>
      <Form
        form={form}
        onFieldsChange={buildBody}
        name={DASHBOARDS_FORMS.EVENT_COUNTER_NUMBER_COMPONENT}
        layout={FORM_LAYOUT_DEFAULT}
        initialValues={{
          title: '',
          description: '',
          divisionId: null,
          formId: null,
          templateIds: [],
          schedulesIds: [],
          locationIds: null,
          triggerId: null,
          userIds: [],
          groupIds: [],
          usersType: USERS_TYPE.ALL,
          type: null,
          formData: {},
        }}
      >
        <BoxContainer content>
          <Row gutter={16}>
            <Col span={24}>
              <KeyValueWidget
                label={
                  <Title.LabelForm
                    required
                    value={intl.formatMessage({ id: 'dashboards.chart.form.title' })}
                  />
                }
                value={
                  <Form.Item
                    name="title"
                    rules={[
                      {
                        required: true,
                        message: intl.formatMessage({ id: 'form.required' }),
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                }
              />
            </Col>
            <Col span={24}>
              <KeyValueWidget
                label={
                  <Title.LabelForm
                    value={intl.formatMessage({ id: 'dashboards.chart.form.description' })}
                  />
                }
                value={
                  <Form.Item name="description">
                    <Input.TextArea showCount rows={4} maxLength={250} />
                  </Form.Item>
                }
              />
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Title.SubTitle
                className="gx-pb-3"
                value={intl.formatMessage({
                  id: 'dashboards.chart.form.counter.filter.by',
                })}
              />
            </Col>
            {!divId && (
              <Col span={24}>
                <KeyValueWidget
                  label={
                    <Title.LabelForm
                      required
                      value={intl.formatMessage({ id: 'general.dropdown.division' })}
                    />
                  }
                  value={
                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.divisionId !== currentValues.divisionId
                      }
                    >
                      {({ getFieldValue }) => {
                        return (
                          <Form.Item name="divisionId" className="gx-m-0">
                            <DivisionsSelector
                              mode="single"
                              divisionIds={getFieldValue('divisionId')}
                              onChange={handleDivisionChange}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                  }
                />
              </Col>
            )}
          </Row>
          <Row gutter={16} className="gx-mt-3">
            <Col span={24}>
              <KeyValueWidget
                label={
                  <Title.LabelForm
                    required
                    value={intl.formatMessage({ id: 'dashboards.chart.form.form' })}
                  />
                }
                value={
                  <Form.Item name="formId">
                    <FormSelect
                      onChange={handleFormChange}
                      options={eventTypes}
                      showSearch
                      loading={eventTypesLoading}
                    />
                  </Form.Item>
                }
              />
            </Col>
            <Col span={24}>
              <Title.LabelForm
                required
                value={intl.formatMessage({ id: 'dashboards.chart.form.component.number' })}
              />
              <Form.Item
                rules={[{ required: true, message: intl.formatMessage({ id: 'form.required' }) }]}
                name="componentId"
              >
                <Select
                  onChange={() => {
                    form.setFieldValue('triggerId', null);
                  }}
                  className="gx-w-100"
                  dropdownMatchSelectWidth={false}
                  optionFilterProp="children"
                  showSearch
                  options={eventTypesWithNumComponents}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <KeyValueWidget
                label={<Title.LabelForm value={intl.formatMessage({ id: 'general.eventTypes' })} />}
                value={
                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) =>
                      prevValues.formId !== currentValues.formId
                    }
                  >
                    {({ getFieldValue }) => {
                      return (
                        <Form.Item name="triggerId">
                          <DropDownTriggers
                            onChange={handleTriggerChange}
                            division={divId || watchDivisionIds}
                            eventType={getFieldValue('formId')}
                            value={getFieldValue('triggerId')}
                            extraTriggers={[]}
                            disabled={!getFieldValue('formId')}
                            allowClear={true}
                            onClear={() => {
                              form.setFieldValue('triggerId', null);
                            }}
                          />
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                }
              />
            </Col>
            <Col span={24} className="gx-mb-3">
              <KeyValueWidget
                label={
                  <>
                    <Title.LabelForm value={intl.formatMessage({ id: 'general.locations' })} />
                  </>
                }
                value={
                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) =>
                      prevValues.divisionId !== currentValues.divisionId
                    }
                  >
                    {({ getFieldValue, setFieldValue }) => {
                      return (
                        <Form.Item name="locationIds">
                          <LocationCombiner
                            value={getFieldValue('locationIds') ?? null}
                            divisionId={divId || watchDivisionIds}
                            onChange={value => setFieldValue('locationIds', value)}
                            showAddButton={false}
                            emptyOptionLabel="general.all"
                          />
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                }
              />
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <KeyValueWidget
                label={
                  <Title.LabelForm
                    required
                    className="gx-mb-2"
                    value={intl.formatMessage({
                      id: 'dashboards.chart.form.counter.filter.information.label',
                    })}
                  />
                }
                value={
                  <DataTypeSelector
                    type={type}
                    selectedSchedules={selectedSchedules}
                    setSelectedSchedules={setSelectedSchedules}
                    setLoadingSchedules={setLoadingSchedules}
                    divisionIds={
                      divId
                        ? [divId]
                        : typeof watchDivisionIds === 'string'
                          ? [watchDivisionIds]
                          : watchDivisionIds
                    }
                    initialScheduleIds={initialScheduleIds}
                    templateIds={fetchedChartData?.queryParams?.templateIds || []}
                    orgId={orgId}
                    form={form}
                    onTypeChange={handleTypeChange}
                    filterDataOptions={filterDataOptions}
                  />
                }
              />
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <KeyValueWidget
                label={
                  <Title.LabelForm
                    required
                    value={intl.formatMessage({
                      id: 'dashboards.chart.form.counter.filter.users.label',
                    })}
                  />
                }
                value={
                  <UsersTypeSelector
                    usersType={watchUsersType}
                    groupOptions={groupOptions}
                    groupsLoading={groupsLoading}
                    handlePopupScrollGroups={handlePopupScrollGroups}
                    onReset={resetUsersAndGroups}
                    orgId={orgId}
                    userIds={fetchedChartData?.queryParams?.userIds || []}
                  />
                }
              />
            </Col>
          </Row>
          <Row gutter={16} className="gx-mt-3">
            <Col span={24}>
              <Title.SubTitle
                className="gx-pb-3"
                value={intl.formatMessage({ id: 'dashboards.chart.form.counter.unit.title' })}
              />
            </Col>
            <Col span={24}>
              <KeyValueWidget
                label={
                  <Title.LabelForm
                    value={intl.formatMessage({ id: 'dashboards.chart.form.counter.unit.label' })}
                  />
                }
                value={
                  <Form.Item name="unit">
                    <Input
                      placeholder={intl.formatMessage({
                        id: 'dashboards.chart.form.counter.unit.placeholder',
                      })}
                    />
                  </Form.Item>
                }
              />
            </Col>
          </Row>
        </BoxContainer>
        <Form.Item name="formData" hidden />
        <Form.Item name="layout" hidden />
        <Form.Item name="id" hidden />
      </Form>
    </Spin>
  );
});

export default CounterNumberComponent;
